/* eslint-disable react-hooks/rules-of-hooks */
import { SelectedMenuProductList, SelectedMenu, SelectedMenuContent } from 'rangeConfig/@types';
import { ProductType } from '@inter-ikea-storageone/range';
import deleteProduct from 'rangeConfig/shared/behaviors/deleteProduct';
import replaceProduct from 'rangeConfig/shared/behaviors/replaceProduct';
import replaceAccessory from 'rangeConfig/pax/behaviors/replaceAccessory';
import removeAccessoriesFromSelectedEntity from 'rangeConfig/pax/behaviors/removeAccessoriesFromSelectedEntity';
import removeInnerLightsFromSelectedEntity from 'rangeConfig/pax/behaviors/removeInnerLightsFromSelectedEntity';
import ChangeOrientation from 'rangeConfig/pax/widgets/ChangeOrientation';
import {
  getIsDrawer,
  getIsShelf,
  getIsDivider,
  getIs580ClothRail
} from 'rangeConfig/pax/utils/utils';
import { getReplaceInteriorSelection } from 'rangeConfig/pax/selection/getReplaceInteriorSelection';
import replaceOrganizerOnShelf from 'rangeConfig/pax/behaviors/replaceOrganizerOnShelf';
import removeOrganizersFromSelectedEntity from 'rangeConfig/pax/behaviors/removeOrganizersFromSelectedEntity';
import platform from 'rangeConfig/shared/utils/platform';
import { noProductsWithTypesForSelectedEntity } from 'rangeConfig/shared/rules/noneOptionSelectedRules';
import {
  matchOrientation,
  matchWidth
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import {
  matchDrawerDimensions,
  matchInteriorType,
  matchLightWidth,
  matchSelectedInteriorDepth,
  matchShelfDimensions
} from 'rangeConfig/pax/filters/selectedMenuProductFilters';
import {
  checkIfChangeOrientationHidden,
  hideInnerLightOnCornerShelf,
  hideInnerLightsMenu
} from 'rangeConfig/pax/rules/hideMenuRules';
import replaceOrganizerOnClothRail from 'rangeConfig/pax/behaviors/replaceOrganizerOnClothRail';
import replaceInnerLights from 'rangeConfig/pax/behaviors/replaceInnerLights';

const interiors: SelectedMenuProductList = {
  id: 'interiors-selected-menu-interiors',
  title: 'storageone.productListInteriorsTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText, communicatedSizeText',
  additionalItemsText: 'storageone.selectedProductAdditionalInteriors',
  productThumbToolTip: 'typeName, designText',
  behaviors: {
    productClicked: [replaceProduct],
    productDropped: [replaceProduct]
  },
  productTypes: [
    ProductType.PAX_DRAWER,
    ProductType.PAX_DRAWER_FRAMED,
    ProductType.PAX_DRAWER_FRAMED_GLASS,
    ProductType.PAX_DRAWER_LOCKABLE,
    ProductType.PAX_GLASS_DRAWER,
    ProductType.PAX_MESH_BASKET_ASM,
    ProductType.PAX_METAL_BASKET_ASM,
    ProductType.PAX_SHELF,
    ProductType.PAX_GLASS_SHELF,
    ProductType.PAX_PULL_OUT_TRAY,
    ProductType.PAX_PULL_OUT_TRAY_WITH_DIVIDER_INSERTS_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_COMPARTMENT_INSERTS_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_SHOE_INSERTS_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_MAT_ASM,
    ProductType.PAX_CLOTH_RAIL_350,
    ProductType.PAX_CLOTH_RAIL_580,
    ProductType.PAX_VALET_HANGER,
    ProductType.PAX_T_DIVIDER,
    ProductType.PAX_L_DIVIDER,
    ProductType.PAX_PULL_OUT_MULTI_USE_HANGER,
    ProductType.PAX_PULL_OUT_TROUSER_HANGER,
    ProductType.PAX_PULL_OUT_CLOTH_RAIL,
    ProductType.PAX_SHOE_SHELF,
    ProductType.PAX_PULL_OUT_SHOE_SHELF,
    ProductType.PAX_PULL_OUT_STORAGE_BAG,
    ProductType.PAX_CLEANING_INTERIOR
  ],
  useMLSorting: true,
  overrideSelection: getReplaceInteriorSelection,
  productFilters: [matchWidth, matchSelectedInteriorDepth, matchOrientation, matchInteriorType]
};

const organizers: SelectedMenuProductList = {
  id: 'interiors-selected-menu-organizers',
  title: 'storageone.selectedProductListAddOrganizers',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText, communicatedSizeText',
  additionalItemsText: 'storageone.selectedProductAdditionalOrganisers',
  behaviors: {
    productClicked: [replaceOrganizerOnShelf],
    productDropped: [replaceOrganizerOnShelf]
  },
  productTypes: [
    ProductType.PAX_SKUBB_STORAGE_CASES,
    ProductType.PAX_SKUBB_SHOE_BOXES,
    ProductType.PAX_HEMMAFIXARE_STORAGE_CASE,
    ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH,
    ProductType.PAX_SKUBB_STORAGE_BOXES,
    ProductType.PAX_HEMMAFIXARE_SHOE_BOX,
    ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP,
    ProductType.PAX_SKUBB_STORAGE_CASE_WRAP
  ],
  hidden: (coreApi, selectedEntityId) =>
    !getIsShelf(coreApi, selectedEntityId) && !getIsDivider(coreApi, selectedEntityId),
  productFilters: [matchShelfDimensions],
  noneOption: {
    behaviors: { removeProducts: [removeOrganizersFromSelectedEntity] },
    selected: noProductsWithTypesForSelectedEntity([
      ProductType.PAX_SKUBB_STORAGE_CASES,
      ProductType.PAX_SKUBB_SHOE_BOXES,
      ProductType.PAX_HEMMAFIXARE_STORAGE_CASE,
      ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH,
      ProductType.PAX_SKUBB_STORAGE_BOXES,
      ProductType.PAX_HEMMAFIXARE_SHOE_BOX,
      ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP,
      ProductType.PAX_SKUBB_STORAGE_CASE_WRAP
    ]),
    title: 'storageone.productListNoShelfOrganisersOptionTitle'
  },
  useMLSorting: true
};

const accessories: SelectedMenuProductList = {
  id: 'interiors-selected-menu-accessories',
  title: 'storageone.productListAccessoriesTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText, communicatedSizeText',
  behaviors: {
    productClicked: [replaceAccessory],
    productDropped: [replaceAccessory]
  },
  productTypes: [ProductType.PAX_DRAWER_MAT, ProductType.PAX_DRAWER_BOX_ASM],
  hidden: (coreApi, selectedEntityId) => !getIsDrawer(coreApi, selectedEntityId),
  productFilters: [matchDrawerDimensions],
  noneOption: {
    behaviors: { removeProducts: [removeAccessoriesFromSelectedEntity] },
    selected: noProductsWithTypesForSelectedEntity([
      ProductType.PAX_DRAWER_MAT,
      ProductType.PAX_DRAWER_BOX_ASM
    ]),
    title: 'storageone.productListNoDrawerOrganisersOptionTitle'
  },
  useMLSorting: true
};

const clothRailOrganisers: SelectedMenuProductList = {
  id: 'interiors-selected-menu-organisers',
  title: 'storageone.selectedProductListAddOrganizers',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText, communicatedSizeText',
  additionalItemsText: 'storageone.selectedProductAdditionalOrganisers',
  behaviors: {
    productClicked: [replaceOrganizerOnClothRail],
    productDropped: [replaceOrganizerOnClothRail]
  },
  productTypes: [ProductType.PAX_SKUBB_HANGING_STORAGE],
  hidden: (coreApi, selectedEntityId) => !getIs580ClothRail(coreApi, selectedEntityId),
  productFilters: [],
  noneOption: {
    behaviors: { removeProducts: [removeOrganizersFromSelectedEntity] },
    selected: noProductsWithTypesForSelectedEntity([ProductType.PAX_SKUBB_HANGING_STORAGE]),
    title: 'storageone.productListNoClothRailOrganisersOptionTitle'
  },
  useMLSorting: true
};

const mobileWidgets = (): SelectedMenuContent[] => {
  return [interiors, organizers, accessories];
};

const desktopWidgets = (): SelectedMenuContent[] => {
  return [
    {
      id: 'change-orientation',
      component: ChangeOrientation,
      hidden: checkIfChangeOrientationHidden
    },
    interiors,
    {
      id: 'interiors-selected-menu-inner-lights',
      title: 'storageone.productListInnerLights',
      cardsTitle: 'name',
      cardsSubtitle: 'typeName, designText, communicatedSizeText',
      behaviors: {
        productClicked: [replaceInnerLights],
        productDropped: [replaceInnerLights]
      },
      productTypes: [ProductType.PAX_INNER_LIGHT, ProductType.PAX_INNER_LIGHT_OVERSIDAN],
      hidden: hideInnerLightsMenu,
      productFilters: [matchLightWidth],
      noneOption: {
        behaviors: { removeProducts: [removeInnerLightsFromSelectedEntity] },
        selected: noProductsWithTypesForSelectedEntity([
          ProductType.PAX_INNER_LIGHT,
          ProductType.PAX_INNER_LIGHT_OVERSIDAN
        ]),
        title: 'storageone.productListNoInnerLightsOptionTitle'
      },
      useMLSorting: true
    },
    {
      id: 'interiors-selected-menu-inner-lights-corner-shelf',
      title: 'storageone.productListInnerLights',
      cardsTitle: 'name',
      cardsSubtitle: 'communicatedSizeText',
      behaviors: {
        productClicked: [replaceInnerLights],
        productDropped: [replaceInnerLights]
      },
      productTypes: [ProductType.PAX_INNER_LIGHT],
      hidden: hideInnerLightOnCornerShelf,
      productFilters: [matchLightWidth],
      noneOption: {
        behaviors: { removeProducts: [removeInnerLightsFromSelectedEntity] },
        selected: noProductsWithTypesForSelectedEntity([ProductType.PAX_INNER_LIGHT]),
        title: 'storageone.productListNoInnerLightsOptionTitle'
      },
      useMLSorting: true
    },
    organizers,
    accessories,
    clothRailOrganisers
  ];
};

const widgets = {
  desktop: desktopWidgets,
  mobile: mobileWidgets
};

const selectedMenu = (): SelectedMenu => {
  return {
    menuTitle: 'storageone.selectedProductListCustomiseInterior',
    behaviors: {
      productDeleted: [deleteProduct]
    },
    content: widgets[platform]()
  };
};

export default selectedMenu;
